import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Avatar, Tooltip,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon              from '@mui/icons-material/ArrowBackRounded';
import TrendingUpRoundedIcon             from '@mui/icons-material/TrendingUpRounded';
import AccountBalanceWalletRoundedIcon   from '@mui/icons-material/AccountBalanceWalletRounded';
import CreditCardRoundedIcon             from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon               from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon            from '@mui/icons-material/PointOfSaleRounded';
import CalendarTodayRoundedIcon          from '@mui/icons-material/CalendarTodayRounded';
import PeopleRoundedIcon                 from '@mui/icons-material/PeopleRounded';
import DownloadRoundedIcon               from '@mui/icons-material/DownloadRounded';
import WorkRoundedIcon                   from '@mui/icons-material/WorkRounded';
import DeleteRoundedIcon                 from '@mui/icons-material/DeleteRounded';
import ChatBubbleOutlineRoundedIcon      from '@mui/icons-material/ChatBubbleOutlineRounded';

import DateQuickPicker from '../../components/DateQuickPicker';
import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetReport, APIGetReportToday, APIExportReportExcel,
    APIGetOrders, APIDeleteOrder, APIGetStockReport,
    APIGetLocations, getToken, API_URL, getAuthConfig,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import { businessTodayISO } from '../../businessDay';


// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

// true, когда ширина экрана меньше брейкпоинта sm (мобильная версия)
function useIsMobile() {
    const query = '(max-width: 599.98px)';
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.matchMedia(query).matches : false
    );
    useEffect(() => {
        const mq = window.matchMedia(query);
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        setIsMobile(mq.matches);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return isMobile;
}

const getInitials = (name = '') => {
    const p = name.trim().split(/\s+/);
    return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase();
};
const MONEY_META = {
    transfer: { label: 'Перевод',  color: '#818cf8', bg: 'rgba(129,140,248,0.12)' },
    cash:     { label: 'Наличные', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
    terminal: { label: 'Терминал', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
};

// Дробное количество к виду "1,8" (без хвостовых нулей); целые — без дробной части
const fmtQty = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v;
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const MEDALS = ['🥇', '🥈', '🥉'];

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};


// ════════════════════════════════════════════════════════════════════
//  StatsPage — сводная статистика выручки
// ════════════════════════════════════════════════════════════════════
export default function StatsPage() {
    const { token, showToast, onRefreshTick, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canExcel = hasPerm('reports.excel');
    const canViewSalary = hasPerm('reports.view');


    // ── Состояние ─────────────────────────────────────────────────
    const [report,       setReport]       = useState(null);
    const [locations,    setLocations]    = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [downloading,  setDownloading]  = useState(false);
    const [activePeriod, setActivePeriod] = useState('today');
    const [dateFrom,     setDateFrom]     = useState(() => businessTodayISO());
    const [dateTo,       setDateTo]       = useState(() => businessTodayISO());
    const [locationId,   setLocationId]   = useState('');
    const [stockRows, setStockRows] = useState([]);
    const [stockSort, setStockSort] = useState('desc');   // 'desc' | 'asc' | 'default'
    const [filterMoney,  setFilterMoney]  = useState('');   // '' | 'transfer' | 'cash' | 'terminal'
    const [filterPhotographerId, setFilterPhotographerId] = useState(null); // null = все фотографы
    const [ordersList,   setOrdersList]   = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    // Какой чек показывает комментарий (id) либо null
    const [openCommentId, setOpenCommentId] = useState(null);
    const isMobile = useIsMobile();

    // На ПК закрываем поповер по клику вне иконки.
    // На мобиле не закрываем — блок скрывается только повторным тапом на 💬.
    useEffect(() => {
        if (openCommentId === null || isMobile) return;
        const close = () => setOpenCommentId(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [openCommentId, isMobile]);

    // ── Загрузка ──────────────────────────────────────────────────
    const loadReport = useCallback(async () => {
        setLoading(true);
        const params = { date_from: dateFrom, date_to: dateTo };
        if (locationId) params.location_id = locationId;

        const data = await APIGetReport(token, params);
        if (data && !data.error) setReport(data);
        setLoading(false);
    }, [token, dateFrom, dateTo, locationId]);

    useEffect(() => {
        (async () => {
            const locs = await APIGetLocations(token);
            if (Array.isArray(locs)) setLocations(locs);
        })();
    }, [token]);

    useEffect(() => { loadReport(); }, [loadReport]);
    useEffect(() => onRefreshTick(loadReport), [onRefreshTick, loadReport]);

    
    const loadStock = useCallback(async () => {
        const params = { date_from: dateFrom, date_to: dateTo };
        if (locationId) params.location_id = locationId;
        const data = await APIGetStockReport(token, params);
        setStockRows(Array.isArray(data?.items) ? data.items : []);
    }, [token, dateFrom, dateTo, locationId]);

    useEffect(() => { loadStock(); }, [loadStock]);

    const loadOrders = useCallback(async () => {
        setOrdersLoading(true);
        const params = { date_from: dateFrom, date_to: dateTo };
        if (locationId) params.location_id = locationId;
        const data = await APIGetOrders(token, params);
        setOrdersList(Array.isArray(data) ? data : []);
        setOrdersLoading(false);
    }, [token, dateFrom, dateTo, locationId]);

    useEffect(() => { loadOrders(); }, [loadOrders]);
    useEffect(() => onRefreshTick(loadOrders), [onRefreshTick, loadOrders]);


    // ── Быстрый период ───────────────────────────────────────────
    const selectPeriod = (p) => {
        setActivePeriod(p.key);
        setDateFrom(p.from);
        setDateTo(p.to);
    };

    // ── Скачать Excel ─────────────────────────────────────────────
    const handleExcel = async () => {
        setDownloading(true);
        try {
            const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
            if (locationId) params.append('location_id', locationId);
            const url = `${API_URL}/api/finance/reports/excel/?${params.toString()}`;
            const resp = await fetch(url, { headers: { Authorization: `Token ${getToken()}` } });
            if (!resp.ok) throw new Error('Ошибка скачивания');
            const blob = await resp.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `report_${dateFrom}_${dateTo}.xlsx`;
            a.click();
            URL.revokeObjectURL(a.href);
            showToast('Отчёт скачан');
        } catch (e) {
            showToast('Ошибка скачивания Excel', 'danger');
        }
        setDownloading(false);
    };

    // ── Данные из отчёта ──────────────────────────────────────────
    const employees = report?.employees || [];
    const rawTotals = report?.totals || {};

    // Сортировка «Продажи по позициям»: по убыванию / по возрастанию / по умолчанию (по названию)
    const sortedStockRows = useMemo(() => {
        const rows = [...stockRows];
        if (stockSort === 'desc') rows.sort((a, b) => (b.sold_qty || 0) - (a.sold_qty || 0));
        else if (stockSort === 'asc') rows.sort((a, b) => (a.sold_qty || 0) - (b.sold_qty || 0));
        else rows.sort((a, b) => String(a.name).localeCompare(String(b.name), 'ru'));
        return rows;
    }, [stockRows, stockSort]);

    // Превращаем плоские employees в раздельные списки
    const photographers = useMemo(() => employees
        .filter(e => e.as_photographer)
        .map(e => ({
            photographer_id:   e.employee_id,
            photographer_name: e.employee_name,
            photographer_avatar_url: e.employee_avatar_url,
            ...e.as_photographer,
        })), [employees]);

    const administrators = useMemo(() => employees
        .filter(e => e.as_administrator)
        .map(e => ({
            administrator_id:   e.employee_id,
            administrator_name: e.employee_name,
            administrator_avatar_url: e.employee_avatar_url,
            ...e.as_administrator,
        })), [employees]);

    // Маппим totals из ph_*/admin_* в плоские ключи, которые ждёт UI
    const totals = useMemo(() => ({
        revenue:       parseFloat(rawTotals.ph_total       || 0),
        transfer:      parseFloat(rawTotals.ph_transfer    || 0),
        cash:          parseFloat(rawTotals.ph_cash        || 0),
        terminal:      parseFloat(rawTotals.ph_terminal    || 0),
        salary:        parseFloat(rawTotals.ph_salary      || 0),
        rewards:       parseFloat(rawTotals.ph_reward      || 0),
        daily_rewards: parseFloat(rawTotals.ph_daily_reward|| 0),
        fines:         parseFloat(rawTotals.ph_fines       || 0),
        deductions:    parseFloat(rawTotals.ph_deductions  || 0),
        net_pay:       parseFloat(rawTotals.ph_net_pay     || 0),
        // админская часть
        admin_subordinate_total: parseFloat(rawTotals.admin_subordinate_total || 0),
        admin_salary:            parseFloat(rawTotals.admin_salary            || 0),
        admin_reward:            parseFloat(rawTotals.admin_reward            || 0),
        admin_fines:             parseFloat(rawTotals.admin_fines             || 0),
        admin_deductions:        parseFloat(rawTotals.admin_deductions        || 0),
        admin_net_pay:           parseFloat(rawTotals.admin_net_pay           || 0),
        admin_total_pay:         parseFloat(rawTotals.admin_net_pay           || 0),
        combined_net_pay:        parseFloat(rawTotals.combined_net_pay        || 0),
    }), [rawTotals]);

    const ordersFiltered = useMemo(() => {
        let list = ordersList;
        if (filterMoney) list = list.filter(o => o.money_type === filterMoney);
        if (filterPhotographerId) list = list.filter(o => o.photographer === filterPhotographerId);
        return list;
    }, [ordersList, filterMoney, filterPhotographerId]);

    // Имя выбранного фотографа — для заголовка списка чеков
    const filterPhotographerName = useMemo(() => {
        if (!filterPhotographerId) return '';
        const ph = photographers.find(p => p.photographer_id === filterPhotographerId);
        return ph?.photographer_name || '';
    }, [filterPhotographerId, photographers]);

    const ordersGrouped = useMemo(() => {
        const map = {};
        for (const o of ordersFiltered) {
            const k = o.date || 'unknown';
            (map[k] = map[k] || []).push(o);
        }
        return Object.entries(map).sort((a, b) => (b[0] || '').localeCompare(a[0] || ''));
    }, [ordersFiltered]);

    const handleDeleteOrder = async (order) => {
        if (!window.confirm(`Удалить чек на ${fmtMoney(order.value)} ₽?`)) return;
        const res = await APIDeleteOrder(token, order.id);
        if (res?.error) { showToast('Не удалось удалить чек', 'danger'); return; }
        // Сервер удаляет ВЕСЬ чек (все строки одного receipt_uid). Локально убрать
        // одну строку недостаточно, а «Выручка» в карточках считается на сервере
        // (report.totals). Поэтому перезапрашиваем и сводку, и список чеков.
        await Promise.all([loadReport(), loadOrders()]);
        showToast('Чек удалён');
    };

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}>
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ──────────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton variant="plain" onClick={() => navigate('/manager')}
                                sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                            <Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Статистика
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                    Сводка выручки и рейтинг фотографов
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {canExcel && (
                                <Button size="sm" variant="outlined" color="neutral"
                                    startDecorator={<DownloadRoundedIcon sx={{ fontSize: 16 }} />}
                                    loading={downloading} onClick={handleExcel}
                                >
                                    Excel
                                </Button>
                            )}
                            {canViewSalary && (
                                <Button size="sm" variant="soft"
                                    onClick={() => navigate('/manager/salary')}
                                    startDecorator={<WorkRoundedIcon sx={{ fontSize: 16 }} />}
                                    sx={{ fontWeight: 700, fontSize: 12 }}>
                                    Зарплаты
                                </Button>
                            )}
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Фильтры ──────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        p: { xs: 1.5, sm: 2 }, mb: 2.5,
                    }}>
                        {/* Чипы периодов */}
                        <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5, flexWrap: 'wrap' }}>
                            <DateQuickPicker
                                activeKey={activePeriod}
                                onChange={(from, to, key) => {
                                    setActivePeriod(key);
                                    setDateFrom(from);
                                    setDateTo(to);
                                }}
                            />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1 }}>
                            <Input type="date" size="sm" value={dateFrom}
                                onChange={(e) => { setDateFrom(e.target.value); setActivePeriod(''); }}
                            />
                            <Input type="date" size="sm" value={dateTo}
                                onChange={(e) => { setDateTo(e.target.value); setActivePeriod(''); }}
                            />
                            <Select size="sm" value={locationId} onChange={(_, v) => setLocationId(v || '')}
                                placeholder="Все точки"
                            >
                                <Option value="">Все точки</Option>
                                {locations.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
                            </Select>
                        </Box>
                    </Box>
                </motion.div>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2.5}>

                            {/* ── Итого-карточки ──────────────────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.5 }}>
                                    {[
                                        { label: 'Выручка',   money: '',         value: fmtMoney(totals.revenue),  icon: <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />, color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)' },
                                        { label: 'Перевод',   money: 'transfer', value: fmtMoney(totals.transfer),  icon: <CreditCardRoundedIcon sx={{ fontSize: 18 }} />, color: '#818cf8', bg: 'rgba(129,140,248,0.08)' },
                                        { label: 'Наличные',  money: 'cash',     value: fmtMoney(totals.cash),      icon: <PaymentsRoundedIcon sx={{ fontSize: 18 }} />, color: '#22c55e', bg: 'rgba(34,197,94,0.08)' },
                                        { label: 'Терминал',  money: 'terminal', value: fmtMoney(totals.terminal),   icon: <PointOfSaleRoundedIcon sx={{ fontSize: 18 }} />, color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
                                    ].map(s => {
                                        const active = filterMoney === s.money;
                                        return (
                                        <Box key={s.label}
                                            onClick={() => setFilterMoney(s.money)}
                                            sx={{
                                                px: 2, py: 1.8, borderRadius: 'var(--joy-radius-lg)',
                                                border: `1px solid ${active ? s.color : 'var(--joy-palette-divider)'}`,
                                                bgcolor: s.bg, cursor: 'pointer',
                                                boxShadow: active ? `0 0 0 1px ${s.color}` : 'none',
                                                transition: 'all .15s',
                                            }}>
                                            <Box sx={{ color: s.color, mb: 0.5 }}>{s.icon}</Box>
                                            <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>{s.label}</Typography>
                                        </Box>
                                        );
                                    })}
                                </Box>

                                {/* Финансовые итоги */}
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)' },
                                    gap: 1.5, mt: 1.5,
                                }}>
                                    {[
                                        { label: 'ЗП фотографов',  value: totals.salary,         color: '#818cf8', bg: 'rgba(129,140,248,0.08)', sign: '' },
                                        { label: 'Премии',          value: totals.rewards,        color: '#22c55e', bg: 'rgba(34,197,94,0.08)',  sign: '+' },
                                        { label: 'Дн. премии',      value: totals.daily_rewards,  color: '#06b6d4', bg: 'rgba(6,182,212,0.08)',  sign: '+' },
                                        { label: 'Штрафы',          value: totals.fines,          color: '#ef4444', bg: 'rgba(239,68,68,0.08)',  sign: '-' },
                                        { label: 'Списания',        value: totals.deductions,     color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', sign: '-' },
                                        { label: 'К выплате',       value: totals.net_pay,        color: 'var(--joy-palette-primary-300)', bg: 'rgba(162,38,226,0.08)', sign: '' },
                                    ].filter(s => parseFloat(s.value) > 0).map(s => (
                                        <Box key={s.label} sx={{
                                            px: 2, py: 1.4,
                                            borderRadius: 'var(--joy-radius-lg)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            bgcolor: s.bg,
                                        }}>
                                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1 }}>
                                                {s.sign}{fmtMoney(s.value)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                                {s.label}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </motion.div>
                            
                            {/* ── Продажи по позициям ─────────────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    borderRadius: 'var(--joy-radius-xl)',
                                    p: { xs: 1.5, sm: 2 }, mb: 2.5,
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 700, flex: 1 }}>
                                            Продажи по позициям
                                        </Typography>
                                        <Select
                                            size="sm"
                                            value={stockSort}
                                            onChange={(e, v) => v && setStockSort(v)}
                                            sx={{ minWidth: 150 }}
                                        >
                                            <Option value="desc">По убыванию</Option>
                                            <Option value="asc">По возрастанию</Option>
                                            <Option value="default">По умолчанию</Option>
                                        </Select>
                                    </Box>
                                    {sortedStockRows.length === 0 ? (
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', py: 1 }}>
                                            Нет данных за период
                                        </Typography>
                                    ) : (
                                        <Stack spacing={0.6}>
                                            {sortedStockRows.map(it => (
                                                <Box key={it.product_id} sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1,
                                                    px: 1.2, py: 0.8,
                                                    bgcolor: 'var(--joy-palette-background-level2)',
                                                    borderRadius: 'var(--joy-radius-sm)',
                                                }}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0 }}>
                                                        {it.name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                                        продано: <b style={{ color: 'var(--joy-palette-text-primary)' }}>{it.sold_qty}</b>
                                                        {' · '}{Number(it.sold_sum).toLocaleString('ru-RU')} ₽
                                                        {Number(it.written_off) > 0 && (
                                                            <> · списано: <b style={{ color: '#f59e0b' }}>{fmtQty(it.written_off)}</b></>
                                                        )}
                                                    </Typography>
                                                    <Chip size="sm" variant="soft" sx={{
                                                        fontSize: 10, fontWeight: 700,
                                                        bgcolor: 'rgba(162,38,226,0.12)', color: 'var(--joy-palette-primary-300)',
                                                    }}>
                                                        остаток: {it.is_unlimited ? '∞' : fmtQty(it.stock)}
                                                    </Chip>
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                            </motion.div>

                            {/* ── Рейтинг фотографов ─────────────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    borderRadius: 'var(--joy-radius-xl)', overflow: 'hidden',
                                }}>
                                    <Box sx={{ px: 2.5, py: 1.8, borderBottom: '1px solid var(--joy-palette-divider)', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TrendingUpRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                            Рейтинг фотографов
                                        </Typography>
                                        <Chip size="sm" variant="soft" color="primary" sx={{ ml: 'auto', fontWeight: 700, fontSize: 11 }}>
                                            {photographers.length}
                                        </Chip>
                                    </Box>

                                    {photographers.length === 0 ? (
                                        <Box sx={{ py: 5, textAlign: 'center' }}>
                                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                                Нет данных за выбранный период
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Stack spacing={0}>
                                            {photographers.map((ph, idx) => {
                                                const total = parseFloat(ph.total) || 0;
                                                const maxTotal = parseFloat(photographers[0]?.total) || 1;
                                                const barWidth = Math.max(4, (total / maxTotal) * 100);

                                                return (
                                                    <Box key={ph.photographer_id}
                                                        onClick={() => setFilterPhotographerId(prev => prev === ph.photographer_id ? null : ph.photographer_id)}
                                                        title="Показать только чеки этого фотографа"
                                                        sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                                        px: 2, py: 1.4, position: 'relative', cursor: 'pointer',
                                                        borderBottom: idx < photographers.length - 1 ? '1px solid var(--joy-palette-divider)' : 'none',
                                                        transition: 'background .15s',
                                                        bgcolor: filterPhotographerId === ph.photographer_id ? 'rgba(162,38,226,0.10)' : 'transparent',
                                                        '&:hover': { bgcolor: filterPhotographerId === ph.photographer_id ? 'rgba(162,38,226,0.14)' : 'var(--joy-palette-background-level2)' },
                                                    }}>
                                                        {/* Фоновый бар */}
                                                        <Box sx={{
                                                            position: 'absolute', left: 0, top: 0, bottom: 0,
                                                            width: `${barWidth}%`,
                                                            bgcolor: idx === 0 ? 'rgba(162,38,226,0.06)'
                                                                : idx === 1 ? 'rgba(129,140,248,0.04)'
                                                                : idx === 2 ? 'rgba(245,158,11,0.04)' : 'transparent',
                                                            transition: 'width .4s ease',
                                                        }} />

                                                        {/* Место */}
                                                        <Typography sx={{
                                                            fontSize: idx < 3 ? 18 : 13,
                                                            fontWeight: 800, minWidth: 28, textAlign: 'center',
                                                            color: idx < 3 ? 'var(--joy-palette-text-primary)' : 'var(--joy-palette-text-tertiary)',
                                                            zIndex: 1,
                                                        }}>
                                                            {MEDALS[idx] || `${idx + 1}`}
                                                        </Typography>

                                                         {/* Аватар */}
                                                        <Avatar src={ph.photographer_avatar_url} size="sm" sx={{
                                                            width: 34, height: 34, fontSize: 12, fontWeight: 700,
                                                            bgcolor: idx < 3 ? 'rgba(162,38,226,0.2)' : 'var(--joy-palette-background-level3)',
                                                            color: idx < 3 ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-tertiary)',
                                                            border: `1.5px solid ${idx < 3 ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)'}`,
                                                            flexShrink: 0, zIndex: 1,
                                                        }}>
                                                            {getInitials(ph.photographer_name)}
                                                        </Avatar>

                                                        {/* Имя + детали */}
                                                        <Box sx={{ flex: 1, minWidth: 0, zIndex: 1 }}>
                                                            <Typography sx={{
                                                                fontSize: 13, fontWeight: 700,
                                                                color: 'var(--joy-palette-text-primary)',
                                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                            }}>
                                                                {ph.photographer_name}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.3, flexWrap: 'wrap' }}>
                                                                <Typography sx={{ fontSize: 11, color: '#818cf8' }}>
                                                                    {fmtMoney(ph.transfer)} пер.
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 11, color: '#22c55e' }}>
                                                                    {fmtMoney(ph.cash)} нал.
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 11, color: '#f59e0b' }}>
                                                                    {fmtMoney(ph.terminal)} терм.
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                    · {ph.work_days} дн.
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {/* Премия */}
                                                        {parseFloat(ph.reward) + parseFloat(ph.daily_reward) > 0 && (
                                                            <Chip size="sm" variant="soft" sx={{
                                                                fontSize: 10, fontWeight: 700, flexShrink: 0, zIndex: 1,
                                                                bgcolor: 'rgba(34,197,94,0.10)', color: '#22c55e',
                                                            }}>
                                                                +{fmtMoney(parseFloat(ph.reward) + parseFloat(ph.daily_reward))}
                                                            </Chip>
                                                        )}

                                                        {/* Штрафы */}
                                                        {parseFloat(ph.fines) + parseFloat(ph.deductions) > 0 && (
                                                            <Chip size="sm" variant="soft" sx={{
                                                                fontSize: 10, fontWeight: 700, flexShrink: 0, zIndex: 1,
                                                                bgcolor: 'rgba(239,68,68,0.10)', color: '#ef4444',
                                                            }}>
                                                                -{fmtMoney(parseFloat(ph.fines) + parseFloat(ph.deductions))}
                                                            </Chip>
                                                        )}

                                                        {/* Итого */}
                                                        <Typography sx={{
                                                            fontSize: 16, fontWeight: 800, flexShrink: 0,
                                                            color: 'var(--joy-palette-text-primary)',
                                                            fontVariantNumeric: 'tabular-nums',
                                                            zIndex: 1, minWidth: 70, textAlign: 'right',
                                                        }}>
                                                            {fmtMoney(ph.total)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </Box>
                            </motion.div>

                            {/* ── Чеки ─────────────────────────────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    borderRadius: 'var(--joy-radius-xl)',
                                    overflow: 'hidden',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                                            Чеки{filterMoney ? ` · ${MONEY_META[filterMoney].label}` : ''}{filterPhotographerName ? ` · ${filterPhotographerName}` : ''}
                                        </Typography>
                                        {(filterMoney || filterPhotographerId) && (
                                            <Typography onClick={() => { setFilterMoney(''); setFilterPhotographerId(null); }}
                                                sx={{ fontSize: 12, color: 'var(--joy-palette-primary-300)', cursor: 'pointer', fontWeight: 700 }}>
                                                Сбросить фильтр
                                            </Typography>
                                        )}
                                    </Box>

                                    {ordersLoading ? (
                                        <Box sx={{ textAlign: 'center', py: 4, color: 'var(--joy-palette-text-tertiary)' }}>Загрузка…</Box>
                                    ) : ordersFiltered.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 4, color: 'var(--joy-palette-text-tertiary)' }}>Нет чеков за период</Box>
                                    ) : ordersGrouped.map(([day, items]) => (
                                        <Box key={day}>
                                            <Box sx={{
                                                px: 2, py: 0.8, bgcolor: 'var(--joy-palette-background-level2)',
                                                display: 'flex', justifyContent: 'space-between',
                                            }}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                                                    {new Date(day + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    {items.length} чек. · {fmtMoney(items.reduce((s, o) => s + parseFloat(o.value || 0), 0))} ₽
                                                </Typography>
                                            </Box>
                                            {items.map(order => {
                                                const mc = MONEY_META[order.money_type] || MONEY_META.cash;
                                                const hasComment = !!(order.photographer_comment || order.comment);
                                                return (
                                                    <Box key={order.id} sx={{
                                                        borderBottom: '1px solid var(--joy-palette-divider)',
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2,
                                                        }}>
                                                            <Avatar src={order.photographer_avatar_url} size="sm" sx={{
                                                                width: 32, height: 32, fontSize: 11, fontWeight: 700,
                                                                bgcolor: 'rgba(162,38,226,0.15)', color: 'var(--joy-palette-primary-300)',
                                                                border: '1.5px solid var(--joy-palette-primary-500)', flexShrink: 0,
                                                            }}>
                                                                {(order.photographer_name || '').charAt(0).toUpperCase()}
                                                            </Avatar>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {order.photographer_name}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                    {order.location_name}{order.administrator_name ? ` · Админ: ${order.administrator_name}` : ''}
                                                                </Typography>
                                                                {order.money_type === 'transfer' && order.payment_number_phone && (
                                                                    <Typography sx={{ fontSize: 10, color: '#818cf8', fontWeight: 600, mt: 0.2 }}>
                                                                        Перевод на: {order.payment_number_bank_display ? `${order.payment_number_bank_display} · ` : ''}{order.payment_number_phone}{order.payment_number_owner ? ` (${order.payment_number_owner})` : ''}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Chip size="sm" variant="soft" sx={{ fontSize: 10, fontWeight: 700, bgcolor: mc.bg, color: mc.color, flexShrink: 0 }}>
                                                                {mc.label}
                                                            </Chip>
                                                            <Typography sx={{ fontSize: 15, fontWeight: 800, minWidth: 60, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                                {fmtMoney(order.value)}
                                                            </Typography>
                                                            {hasComment && (
                                                                isMobile ? (
                                                                    <IconButton
                                                                        size="sm"
                                                                        variant={openCommentId === order.id ? 'soft' : 'plain'}
                                                                        onClick={() => setOpenCommentId(prev => prev === order.id ? null : order.id)}
                                                                        sx={{
                                                                            color: '#f59e0b', flexShrink: 0,
                                                                            bgcolor: openCommentId === order.id ? 'rgba(245,158,11,0.15)' : 'transparent',

                                                                        }}
                                                                    >
                                                                        <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18, color: '#edf871' }} />
                                                                    </IconButton>
                                                                ) : (
                                                                    <Tooltip
                                                                        arrow
                                                                        placement="left"
                                                                        variant="outlined"
                                                                        open={openCommentId === order.id}
                                                                        onClose={() => setOpenCommentId(null)}
                                                                        title={
                                                                            <Box
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                sx={{ maxWidth: 260, p: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                                                            >
                                                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', mb: 0.3 }}>
                                                                                    Комментарий фотографа
                                                                                </Typography>
                                                                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)' }}>
                                                                                    {order.photographer_comment || order.comment}
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                    >
                                                                        <IconButton
                                                                            size="sm"
                                                                            variant="plain"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setOpenCommentId(prev => prev === order.id ? null : order.id);
                                                                            }}
                                                                            sx={{ bgcolor: 'rgba(248, 219, 113, 0.5)', '&:hover': { bgcolor: 'rgba(241, 248, 113, 0.18)' } }}
                                                                        >
                                                                            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18, color: '#edf871' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )
                                                            )}
                                                            <IconButton size="sm" variant="solid" color="danger" onClick={() => handleDeleteOrder(order)}
                                                                sx={{ bgcolor: 'rgba(248,113,113,0.5)', '&:hover': { bgcolor: 'rgba(248,113,113,0.18)' } }}>
                                                                <DeleteRoundedIcon sx={{ fontSize: 18, color: '#f87171' }} />
                                                            </IconButton>
                                                        </Box>
                                                        {/* Мобайл: комментарий выезжает снизу карточки, скрывается повторным тапом на 💬 */}
                                                        <AnimatePresence>
                                                            {isMobile && hasComment && openCommentId === order.id && (
                                                                <motion.div
                                                                    initial={{ height: 0, opacity: 0 }}
                                                                    animate={{ height: 'auto', opacity: 1 }}
                                                                    exit={{ height: 0, opacity: 0 }}
                                                                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                                                                    style={{ overflow: 'hidden' }}
                                                                >
                                                                    <Box sx={{
                                                                        mx: 2, mb: 1.4, mt: 0.2, p: 1.2,
                                                                        bgcolor: 'var(--joy-palette-background-level2)',
                                                                        border: '1px solid var(--joy-palette-divider)',
                                                                        borderLeft: '2px solid #f59e0b',
                                                                        borderRadius: 'var(--joy-radius-sm)',
                                                                    }}>
                                                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', mb: 0.3 }}>
                                                                            Комментарий фотографа
                                                                        </Typography>
                                                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                            {order.photographer_comment || order.comment}
                                                                        </Typography>
                                                                    </Box>
                                                                </motion.div>
                                                            )}
                                                        </AnimatePresence>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    ))}
                                </Box>
                            </motion.div>

                            {/* ── Администраторы ──────────────────── */}
                            {administrators.length > 0 && (
                                <motion.div variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)', overflow: 'hidden',
                                    }}>
                                        <Box sx={{ px: 2.5, py: 1.8, borderBottom: '1px solid var(--joy-palette-divider)', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PeopleRoundedIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                Администраторы
                                            </Typography>
                                        </Box>

                                        <Stack spacing={0}>
                                            {administrators.map((adm, idx) => (
                                                <Box key={adm.administrator_id} sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                                    px: 2, py: 1.4,
                                                    borderBottom: idx < administrators.length - 1 ? '1px solid var(--joy-palette-divider)' : 'none',
                                                }}>
                                                    <Avatar src={adm.administrator_avatar_url} size="sm" sx={{
                                                        width: 32, height: 32, fontSize: 11, fontWeight: 700,
                                                        bgcolor: 'rgba(6,182,212,0.15)', color: '#06b6d4',
                                                        border: '1.5px solid rgba(6,182,212,0.4)',
                                                    }}>
                                                        {getInitials(adm.administrator_name)}
                                                    </Avatar>

                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                            {adm.administrator_name}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                            {adm.work_days} дн.
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#06b6d4', fontVariantNumeric: 'tabular-nums' }}>
                                                            {adm.subordinate_total != null ? fmtMoney(adm.subordinate_total) : '—'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                            выручка
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Stack>

                                        {/* Итого по админам */}
                                        <Box sx={{
                                            px: 2, py: 1.2,
                                            borderTop: '1px solid var(--joy-palette-divider)',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            bgcolor: 'rgba(6,182,212,0.04)',
                                        }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)' }}>
                                                Итого выручка
                                            </Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#06b6d4', fontVariantNumeric: 'tabular-nums' }}>
                                                {fmtMoney(totals.admin_subordinate_total || administrators.reduce((s, a) => s + parseFloat(a.subordinate_total || 0), 0))}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            )}
                        </Stack>
                    </motion.div>
                )}
            </Box>
        </motion.div>
    );
}